// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import JettingAppJetRow from "@stoneage/App/Components/JettingAppComponents/JettingAppJetRow";
import { JetArray } from "@stoneage/App/Services/JettingApp/JettingAppService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { xParserOptions } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { ChangeEvent, useEffect, useState } from "react";

interface Props {
    jetArray: JetArray[];
    jetOptions: string[];
    tooltipText: string;
    readOnly: boolean;
    onChange: (jetArray: JetArray[]) => void;
    extendedStyles?: JettingAppJettingStyles;
}

export interface JettingAppJettingStyles {
    wrapper?: InjectableCss;
    header?: InjectableCss;
    rowWrapper?: InjectableCss;
    angle?: InjectableCss;
    jets?: InjectableCss;
    qty?: InjectableCss;
    size?: InjectableCss;
    flow?: InjectableCss;
    tooltip?: TooltipPresentationProps;
}

export const jettingAppJettingStyles: JettingAppJettingStyles = {};

const JettingAppJetting = ({ extendedStyles, onChange, jetArray, jetOptions, tooltipText, readOnly }: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppJettingStyles, extendedStyles));
    const [localJetArray, setLocalJetArray] = useState<JetArray[]>(jetArray);
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    useEffect(() => {
        if (localJetArray === jetArray) {
            return;
        }
        setLocalJetArray(jetArray);
    }, [jetArray]);

    const onDropDownChange = (value: string, index: number) => {
        // here we need to invoke jettingappchange function
        const newJetArray = localJetArray;
        newJetArray[index] = { ...newJetArray[index], jetPart: value };
        setLocalJetArray(newJetArray);
        onChange(newJetArray);
    };

    if (!jetArray || !jetArray.length || !jetOptions) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.header}>
                <StyledWrapper {...styles.angle}>{translate("Angle")}</StyledWrapper>
                {!isTabletScreen && <StyledWrapper {...styles.jets}>{translate("Jets")}</StyledWrapper>}
                <StyledWrapper {...styles.qty}>{translate("Qty")}</StyledWrapper>
                <StyledWrapper {...styles.size}>
                    {translate("Size")}
                    {tooltipText && (
                        <Tooltip {...styles.tooltip} passAsChildren={true}>
                            <Typography>{parse(tooltipText, xParserOptions)}</Typography>
                        </Tooltip>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.flow}>{translate("Flow %")}</StyledWrapper>
            </StyledWrapper>
            {localJetArray.map((row, index) => {
                return (
                    <StyledWrapper {...styles.rowWrapper} key={index}>
                        <JettingAppJetRow
                            jetOptions={jetOptions}
                            row={row}
                            tooltipText={tooltipText}
                            readOnly={readOnly}
                            onChange={value => onDropDownChange(value, index)}
                        />
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};
export default JettingAppJetting;
